/* eslint-disable max-params */
import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import { Modal, Button, message, Dropdown } from "antd";
import { useTranslation } from "react-i18next";
import logger from "@/utils/logger";
import { detectOS, formatPercent, getWebEnvObj, IFocusSDK, isLinuxOs } from "@/utils";
import { checkDownloadAvailable, checkUpgradeApp } from "@/api/upgradeApp";
import { getVersionInfo } from "@/utils/getVersion";
import "./index.less";
import AppRuntimeEnv from "@/types/AppRuntimeEnv";
import { changeEgovUrl } from "@/utils/tools";
import { analysisLog } from "@/utils/logAnalytics";
import { debounce, omit } from "lodash";
import { connect } from "dvajs";
import ImService from "@/server/ImService";
import { getTime, isSameDay } from "@/utils/date";
import { usePrevious } from "@/utils/chat/index";
import dayjs from "dayjs";
import IconFont from "../icon";
import { UpgradePopType } from "@/types/common";

const log = logger.getLogger("UpgradeModal");

interface UpgradeModalProps {
  focusSDK: IFocusSDK;
  upgradeVersionInfo: string[];
  appRuntimeEnv: AppRuntimeEnv | undefined;
  updateUpgradeState: Function;
  userInfo: any;
  upgradeState: any;
  mainWinShow: boolean;
}

const KEY_UPGRADE_VER = "upgrade_version";

// 12小时
const CHECK_TIME = 12 * 60 * 60 * 1000;

const START_TIME = "00:00";
const END_TIME = "23:59";

const LIST_TIP_LATER = [
  {
    value: 1,
    label: "1小时后"
  },
  {
    value: 2,
    label: "2小时后"
  },
  {
    value: 4,
    label: "4小时后"
  },
  {
    value: 0,
    label: "今日不再提示"
  }
];

let lockCheckUpgrade = false;
// eslint-disable-next-line complexity
function UpgradeModal(props: UpgradeModalProps) {
  const { updateUpgradeState, userInfo, mainWinShow, upgradeState } = props;
  const localUrl = new URL(location.href);
  const pageName = localUrl.searchParams.get("page");
  const isHomePage = !pageName;
  const { focusSDK, appRuntimeEnv } = props;
  const { t } = useTranslation("common");
  const [releaseNotes, setReleaseNotes] = useState<string[]>([]);

  const { type: upgradeType, data } = upgradeState?.upgradeData || {};
  const { version = "", percent, updateTitle = "", fileName = "" } = data || {};
  const percentStr = formatPercent(percent);

  const [forceUpdate, setForceUpdate] = useState(false);
  const preMainWinShow = usePrevious(mainWinShow);
  const isBusy = useRef(false);
  const showTimer = useRef<any>(null);
  const limitTimer = useRef<any>(null);
  const visibleRef = useRef(!!upgradeState?.visible);

  let title = updateTitle || t("upgrade.title", { newVersion: "" });
  const canLoad = useRef<boolean>(true);
  const canUpdateAvailable = useRef<boolean>(true);

  // 下拉菜单
  const [dropdownVisable, setDropdownVisable] = useState(false);
  switch (upgradeType) {
    case "checking":
      title = t("upgrade.checking");
      break;
    case "updateAvailable":
    case "updateDownloading":
    case "updateDownloaded":
      title = updateTitle || t("upgrade.title", { newVersion: version ? `V${version}` : "" });
      /*  title = t("upgrade.downloading", {
        percent: `${isNaN(percent) ? 0 : Number(percent).toFixed(2)}%` // eslint-disable-line
      }); */
      break;
    case "updateNotAvailable":
      title = t("upgrade.checkNotAvailable");
      break;
    case "error":
      title = t("upgrade.errorVer", {
        newVersion: upgradeState?.upgradeData?.data?.version
          ? `V${upgradeState?.upgradeData?.data?.version}`
          : ""
      });
      log.error("app autoUpdate error", data);
      break;
    case "macUploadInstallStart":
      title = "正在为您安装，请不要关闭程序";
      break;
    case "macUploadInstallEnd":
      title = "安装完成，请关闭并重启程序";
      break;
    default:
      title = updateTitle || t("upgrade.title", { newVersion: version ? `V${version}` : "" });
      break;
  }

  // 检测
  const getShowUpgradeTip = useCallback(() => {
    const checkTime = upgradeState?.checkTime || 0;
    const diff = new Date().getTime() - checkTime;
    return !checkTime || diff >= CHECK_TIME;
  }, [upgradeState]);

  // 提醒
  const getShowUpgradeTipLocal = useCallback(async (status) => {
    if ([UpgradePopType.NOTIP, UpgradePopType.REDDOT].includes(status.popType || 0)) {
      return {
        canShow: false
      };
    }
    const versionInfo = getVersionInfo(focusSDK);
    const buildVersion = Number(versionInfo?.buildVersion);
    try {
      const data = await ImService.getInstance()?.getAppModuleVersion(KEY_UPGRADE_VER);
      if (!data) {
        return {
          canShow: true
        };
      }
      const res = JSON.parse(data);
      if (parseInt(res.buildVersion, 10) < buildVersion) {
        return {
          canShow: true
        };
      }
      if (parseInt(res.buildVersion, 10) < (status?.buildVersion || 0)) {
        return {
          canShow: true
        };
      }
      if (!res?.laterTime) {
        return {
          canShow: !isSameDay(parseInt(res.date, 10), new Date().getTime())
        };
      } else {
        return {
          canShow: true,
          laterTime: parseInt(res?.laterTime, 10)
        };
      }
    } catch (e) {
      return {
        canShow: true
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 设置本地版本，时间
  const setUpgradeVersion = (buildVersion: any, laterTime = 0) => {
    const date = new Date().getTime();
    ImService.getInstance()?.setAppVersion(
      JSON.stringify({
        date,
        buildVersion,
        laterTime
      }),
      KEY_UPGRADE_VER
    );
  };

  const onUpdaterMessage = useCallback(
    async (info: any) => {
      if (!isHomePage) {
        return;
      }
      const newData = {
        ...upgradeState?.upgradeData,
        ...info,
        data: { ...upgradeState?.upgradeData?.data, ...omit(info.data, "version") }
      };
      // 下载中报错，通知下载停止
      if (upgradeType === "updateDownloading" && info.type === "error") {
        checkDownloadAvailable({ downloadFlag: 1 }).then();
      }
      updateUpgradeState({
        upgradeData: newData
      });
      // 强制更新，弹窗
      const isForce = String(upgradeState.updateType) === "4";
      if (
        ["updateNotAvailable", "patchAvailable", "updateAvailable", "checking", "error"].indexOf(
          info.type
        ) >= 0
      ) {
        if (!upgradeState.inAutoBackground || isForce) {
          updateUpgradeState({
            visible: true
          });
          visibleRef.current = true;
          // 设置本地版本
          setUpgradeVersion(newData?.newBuildVersion);
        } else if (["updateAvailable"].includes(info.type)) {
          checkAndDownload();
        }
      }
      if (["updateDownloaded"].indexOf(info.type) >= 0) {
        // 通知下载停止
        checkDownloadAvailable({ downloadFlag: 1 }).then();
        const { canShow, laterTime = 0 } = await getShowUpgradeTipLocal(upgradeState);
        if (upgradeState.inAutoBackground && canShow) {
          const time = isInShowTime({
            popType: upgradeState?.popType || 0,
            popStartTime: upgradeState?.popStartTime || START_TIME,
            popEndTime: upgradeState?.popEndTime || END_TIME,
            laterTime
          });
          if (time === 0) {
            updateUpgradeState({
              inAutoBackground: false,
              visible: true
            });
            // 设置本地版本
            setUpgradeVersion(newData?.newBuildVersion);
            visibleRef.current = true;
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isHomePage, upgradeState, upgradeType, updateUpgradeState, getShowUpgradeTipLocal]
  );

  // 后台
  const onBackground = useCallback(() => {
    updateUpgradeState({
      visible: false,
      backgroundDownload: true
    });
  }, [updateUpgradeState]);

  const createConfirm = useCallback(() => {
    Modal.confirm({
      content: "是否取消下载？",
      okText: "确认",
      cancelText: "取消",
      onOk: async () => {
        handleStateClear(upgradeState?.upgradeData, "updateAvailable");
        focusSDK.sendMainMessage("autoUpdate", { action: "abort" });
        updateUpgradeState({
          visible: false
        });
        visibleRef.current = false;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateUpgradeState, upgradeState?.upgradeData]);

  // 取消
  const onCancel = useCallback(
    (prompt = false) => {
      if (forceUpdate) {
        return;
      }
      // 下载中提示
      if (prompt && ["updateDownloading"].includes(upgradeType)) {
        createConfirm();
      } else {
        // focusSDK.sendMainMessage("autoUpdate", { action: "abort" });
        updateUpgradeState({
          visible: false
        });
        visibleRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createConfirm, forceUpdate, updateUpgradeState, upgradeType]
  );

  const doCheckUpdate = useCallback(
    (version: string, newBuildVersion: string, type = 1, downloadUrl = "") => {
      if (visibleRef.current) {
        return;
      }
      if (isBusy.current) {
        doCheckUpdateDelay(10 * 1000, version, newBuildVersion, type, downloadUrl);
      } else {
        focusSDK.checkUpdate(true, version, `${newBuildVersion}`, type, downloadUrl);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [upgradeType]
  );

  const doCheckUpdateDelay = (
    delay: number,
    version: string,
    newBuildVersion: string,
    type = 1,
    downloadUrl = ""
  ) => {
    if (showTimer.current) {
      clearTimeout(showTimer.current);
    }
    showTimer.current = setTimeout(() => {
      doCheckUpdate(version, newBuildVersion, type, downloadUrl);
    }, delay);
  };

  // 弹窗时机
  const isInShowTime = useCallback((updState: any) => {
    // 不弹窗
    if ([UpgradePopType.NOTIP, UpgradePopType.REDDOT].includes(updState?.popType || 0)) {
      return -1;
    }
    const [d1, d2] = (updState?.popStartTime || START_TIME).split(":");
    const [d3, d4] = (updState?.popEndTime || END_TIME).split(":");
    const laterTime = updState.laterTime;
    const start = dayjs().hour(parseInt(d1, 10)).minute(parseInt(d2, 10)).toDate().getTime();
    const end = dayjs().hour(parseInt(d3, 10)).minute(parseInt(d4, 10)).toDate().getTime();
    const time = new Date().getTime();
    const diff = start - time;
    const diffLocal = !laterTime ? 0 : laterTime < time ? 0 : laterTime - time;
    if (time >= start && time <= end) {
      if (laterTime) {
        return diffLocal;
      } else {
        return 0;
      }
    } else if (diff > 0) {
      return diff;
    } else {
      return diffLocal;
    }
  }, []);

  const checkUpgrade = useCallback(async () => {
    const autoDownload = appRuntimeEnv?.registryConfig?.settings?.autoUpgradeDownloads;
    if (!isHomePage || ["updateDownloading"].includes(upgradeType)) {
      return;
    }
    const versionInfo = getVersionInfo(focusSDK);
    const inputVersion = versionInfo?.version;
    const buildVersion = Number(versionInfo?.buildVersion);
    if (inputVersion && appRuntimeEnv?.platform) {
      const versionSplits = inputVersion.split("-");
      const [data, error] = await checkUpgradeApp(
        versionSplits[0],
        appRuntimeEnv.platform,
        buildVersion
      );
      let {
        updateType,
        releaseNotes,
        version,
        title,
        buildVersion: newBuildVersion,
        fileName,
        popStartTime,
        popEndTime,
        popTitle = "",
        popType = 0,
        textChainList = [],
        downUrl: downloadUrl
      } = data || {};
      let newState;
      if (data) {
        // 1. 有更新
        newState = {
          show:
            [UpgradePopType.POPTIP, UpgradePopType.REDDOT, UpgradePopType.NONE].includes(popType) ||
            popType === null,
          currVersion: versionSplits[0],
          currBuildVersion: buildVersion,
          newVersion: version,
          buildVersion: newBuildVersion,
          checkTime: new Date().getTime(),
          popStartTime: popStartTime || START_TIME,
          popEndTime: popEndTime || END_TIME,
          inAutoBackground: false,
          popTitle,
          // 最多5条
          textChainList: textChainList?.slice(0, 5) || [],
          popType,
          downloadUrl
        };
        // 自动下载
        if (autoDownload) {
          focusSDK.checkUpdate(true, version, `${newBuildVersion}`, updateType, downloadUrl);
          newState.inAutoBackground = true;
        }
      } else {
        newState = {
          currVersion: versionSplits[0],
          checkTime: new Date().getTime()
        };
      }
      const newData = {
        version,
        updateTitle: title,
        fileName,
        buildVersion,
        newBuildVersion,
        downloadUrl
      };
      lockCheckUpgrade = false;
      updateUpgradeState({
        ...newState,
        updateType,
        upgradeData: {
          data: newData
        }
      });
      const tempUpdateType = String(updateType);
      try {
        const tempReleaseNotes = releaseNotes.split(/\n/);
        setReleaseNotes(tempReleaseNotes);
      } catch (error) {}
      if (tempUpdateType === "1") {
        const { canShow, laterTime = 0 } = await getShowUpgradeTipLocal(newState);
        if (!canShow) {
          return;
        }
        // 弹窗
        const time = isInShowTime({
          popType,
          popStartTime: popStartTime || START_TIME,
          popEndTime: popEndTime || END_TIME,
          laterTime
        });
        if (time === 0) {
          doCheckUpdate(version, newBuildVersion, 1, downloadUrl);
        } else if (time > 0) {
          doCheckUpdateDelay(time, version, newBuildVersion, 1);
        }
      } else if (tempUpdateType === "3" || tempUpdateType === "4") {
        focusSDK.checkUpdate(true, version, `${newBuildVersion}`, updateType, downloadUrl);
        setForceUpdate(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appRuntimeEnv, isHomePage, upgradeState, upgradeType]);

  // 窗口恢复检测
  const doOnMainWinShow = debounce(checkUpgrade, 100);

  const isMainShow = useMemo(() => {
    return preMainWinShow === false && mainWinShow;
  }, [preMainWinShow, mainWinShow]);

  const showLimitDelayTime = useMemo(() => {
    if (!upgradeState.limitDelayTime) {
      return "";
    }
    const res = getTime(upgradeState.limitDelayTime);
    return `${res?.time}${res?.unit}`;
  }, [upgradeState.limitDelayTime]);

  // 弹窗标题
  const popTitleTxt = useMemo(() => {
    if (!["error"].includes(upgradeType)) {
      return upgradeState.popTitle || t("upgrade.popTitle");
    } else {
      return t("upgrade.errorUpdate");
    }
  }, [upgradeType, upgradeState.popTitle, t]);

  const releaseNotesTxt = useMemo(() => {
    if (!["error"].includes(upgradeType)) {
      return releaseNotes;
    } else {
      return ["请检查网络设置或点击重试"];
    }
  }, [releaseNotes, upgradeType]);

  // 版本信息
  const versionInfoComp = useMemo(() => {
    const { currBuildVersion = 0, currVersion, buildVersion = 0, newVersion } = upgradeState;
    return (
      <div className="version-info">
        <span>{`当前版本${currVersion}${
          currBuildVersion ? `.${`${currBuildVersion}`.slice(-3)}` : ""
        }`}</span>
        {newVersion ? (
          <span>{`更新版本${newVersion}${
            buildVersion ? `.${`${buildVersion}`.slice(-3)}` : ""
          }`}</span>
        ) : null}
      </div>
    );
  }, [upgradeState]);

  const popTitleComp = useMemo(() => {
    if (["error"].includes(upgradeType)) {
      return (
        <div className="error-title ellipsis">
          <IconFont type="iconpc_ic_shibai_doc" />
          <div className="text">{popTitleTxt}</div>
        </div>
      );
    } else {
      return <div className="pop-title ellipsis">{popTitleTxt}</div>;
    }
  }, [upgradeType, popTitleTxt]);

  useEffect(() => {
    const isInMainPage = location.href.match(/messages/);
    if (isMainShow && !visibleRef.current && isInMainPage) {
      const res = getShowUpgradeTip();
      if (res) {
        doOnMainWinShow();
      }
    }
  }, [isMainShow, getShowUpgradeTip, doOnMainWinShow]);

  useEffect(() => {
    if (!isHomePage || (!isLinuxOs() && !navigator.onLine) || !userInfo?.userId) {
      return;
    }

    if (!lockCheckUpgrade) {
      lockCheckUpgrade = true;
      const stt = setTimeout(() => {
        clearTimeout(stt);
        checkUpgrade();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHomePage, userInfo?.userId]);

  useEffect(() => {
    const autoDownload = appRuntimeEnv?.registryConfig?.settings?.autoUpgradeDownloads;
    updateUpgradeState({
      autoUpgradeDownloads: autoDownload
    });
  }, [appRuntimeEnv, updateUpgradeState]);

  // 重试
  const handleRetry = useCallback((data: any) => {
    handleStateClear(data);
    handleUpdateAvailable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countDownLimitTime = useCallback(
    (time: number) => {
      if (limitTimer.current) {
        clearTimeout(limitTimer.current);
      }
      if (time) {
        limitTimer.current = setTimeout(() => {
          const newTime = time - 1;
          updateUpgradeState({
            limitDelayTime: newTime
          });
          countDownLimitTime(newTime);
        }, 1000);
      }
    },
    [updateUpgradeState]
  );

  // 打开连接
  const handleOpenUrl = useCallback((i: any) => {
    if (i?.url) {
      window.open(changeEgovUrl(i.url));
    }
  }, []);

  // 下拉菜单
  const handleDrowpdownOpen = useCallback((val) => {
    setDropdownVisable(val);
  }, []);

  useEffect(() => {
    if (!isHomePage) {
      return;
    }
    focusSDK.on("updaterMessage", onUpdaterMessage);
    return () => {
      focusSDK.off("updaterMessage", onUpdaterMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUpdaterMessage, isHomePage]);

  const onKeyDown = debounce(() => {
    isBusy.current = true;
  }, 100);

  const onKeyUp = debounce(() => {
    isBusy.current = false;
  }, 1000);

  useEffect(() => {
    if (!isHomePage) {
      return;
    }
    // 按键处理
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHomePage]);

  const handleLater = useCallback((version: any, buildVersion: any, item: any) => {
    if (item?.value === 0) {
      // 关闭
      setUpgradeVersion(buildVersion);
    } else {
      const t = new Date();
      // 设置稍后时间
      const newTime = t.setHours(t.getHours() + item.value);
      setUpgradeVersion(buildVersion, newTime);
      doCheckUpdateDelay(item?.value * 1000 * 60 * 60, version, buildVersion);
    }
    updateUpgradeState({
      visible: false
    });
    visibleRef.current = false;
    handleDrowpdownOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buttonLatyerComp = useMemo(() => {
    return (
      <Dropdown
        trigger={["click"]}
        placement="bottomCenter"
        onVisibleChange={handleDrowpdownOpen}
        overlay={
          <div className="upgrade-dropdown-content">
            {LIST_TIP_LATER.map((item, idx) => {
              return (
                <div
                  className="dropdown-item"
                  key={`item-${idx}`}
                  onClick={() =>
                    handleLater(upgradeState.newVersion, upgradeState.buildVersion, item)
                  }
                >
                  {item.label}
                </div>
              );
            })}
          </div>
        }
      >
        <div className="btn plan">
          {t("upgrade.later")}
          <IconFont type={dropdownVisable ? "iconicon_up" : "iconicon_down"} />
        </div>
      </Dropdown>
    );
  }, [
    dropdownVisable,
    handleDrowpdownOpen,
    handleLater,
    t,
    upgradeState.buildVersion,
    upgradeState.newVersion
  ]);

  const downloadPatch = useCallback(() => {
    if (!canLoad.current) return;
    canLoad.current = false;
    setTimeout(() => {
      canLoad.current = true;
    }, 2000);
    focusSDK.sendMainMessage("autoUpdate", {
      action: "downloadPatch"
    });
  }, [focusSDK]);

  const doPatch = useCallback(() => {
    if (!canLoad.current) return;
    canLoad.current = false;
    setTimeout(() => {
      canLoad.current = true;
    }, 5000);
    focusSDK.sendMainMessage("autoUpdate", {
      action: "doPatch"
    });
  }, [focusSDK]);

  // 底部按钮
  // eslint-disable-next-line complexity
  const buttonComp = useMemo(() => {
    // 下载中
    if (upgradeType === "updateDownloading") {
      return (
        <div className="progress-footer">
          <div className="progress">
            <div className="inner" style={{ width: `${percentStr}` }} />
          </div>
          <span className="text">{t("upgrade.downloadBtn", { percent: percentStr })}</span>
          {!forceUpdate && (
            <div
              className="btn-text"
              onClick={() => {
                onBackground();
              }}
            >
              {t("upgrade.background")}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="button-footer">
          {/** 下载完成 **/}
          {["updateDownloaded", "installCopyDataStart", "installCopyDataEnd"].includes(
            upgradeType
          ) && (
            <>
              {!forceUpdate && buttonLatyerComp}
              <Button
                className={`btn primary ${forceUpdate ? "large" : ""}`}
                onClick={handleUpdateDownloaded}
                loading={["installCopyDataStart", "installCopyDataEnd"].includes(upgradeType)}
              >
                {["installCopyDataStart", "installCopyDataEnd"].includes(upgradeType) &&
                detectOS() === "Mac"
                  ? t("upgrade.installingAndQuit")
                  : t("upgrade.installAndQuit")}
              </Button>
            </>
          )}
          {upgradeType === "patchAvailable" && (
            <Button
              type="primary"
              block
              className="btn-item"
              onClick={() => {
                downloadPatch();
              }}
            >
              下载更新
            </Button>
          )}
          {upgradeType === "patchDownloading" && (
            <Button className="progress-wrap" block>
              <div className="progress-text">{t("upgrade.downloadBtn", { percent: "" })}</div>.
            </Button>
          )}
          {upgradeType === "patchDownloaded" && (
            <Button block type="primary" className="btn-item" onClick={doPatch}>
              应用更新
            </Button>
          )}

          {/** 可下载 **/}
          {upgradeType === "updateAvailable" && (
            <>
              {!forceUpdate && buttonLatyerComp}
              <Button
                block
                disabled={!!upgradeState.limitDelayTime}
                className={`btn primary ${forceUpdate ? "large" : ""}`}
                onClick={handleUpdateAvailable}
              >
                {`${t("upgrade.update")}${showLimitDelayTime ? `(${showLimitDelayTime})` : ""}`}
              </Button>
            </>
          )}
          {/** 错误 **/}
          {["error"].includes(upgradeType) && (
            <>
              {!forceUpdate && ["error"].includes(upgradeType) && (
                <Button
                  block
                  className="btn plan"
                  onClick={() => {
                    onCancel();
                  }}
                >
                  {t("upgrade.close")}
                </Button>
              )}
              <Button
                block
                className={`btn primary ${forceUpdate ? "large" : ""}`}
                onClick={() => handleRetry(upgradeState?.upgradeData)}
              >
                {t("upgrade.retry")}
              </Button>
            </>
          )}
          {/** 无升级 */}
          {["updateNotAvailable"].includes(upgradeType) && (
            <Button
              block
              className="btn primary large"
              onClick={() => {
                onCancel();
              }}
            >
              {t("upgrade.close")}
            </Button>
          )}
          {/** mac更新 */}
          {["macUploadInstallEnd"].includes(upgradeType) && (
            <Button
              block
              className="btn primary large"
              onClick={() => {
                focusSDK.sendMainMessage("autoUpdate", {
                  action: "quitAfterInstall"
                });
              }}
            >
              关闭程序
            </Button>
          )}
        </div>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    upgradeType,
    percentStr,
    forceUpdate,
    downloadPatch,
    doPatch,
    upgradeState,
    showLimitDelayTime,
    buttonLatyerComp
  ]);

  const relaseNotesStyle = useMemo(() => {
    const len = (upgradeState?.textChainList || []).length;
    return {
      height: 240 - Math.min(len, 2) * 30
    };
  }, [upgradeState.textChainList]);

  if (!isHomePage) {
    return null;
  }

  function handleUpdateDownloaded() {
    if (!canLoad.current) return;
    canLoad.current = false;
    setTimeout(() => {
      canLoad.current = true;
    }, 2000);
    focusSDK.sendMainMessage("autoUpdate", {
      action: "quitAndInstall"
    });
  }

  async function checkAndDownload() {
    const [data, error] = await checkDownloadAvailable({
      downloadFlag: 0
    });
    if (data) {
      analysisLog("xtbg_jzb_download_package", fileName);
      focusSDK.sendMainMessage("autoUpdate", { action: "download" });
    } else {
      // 非后台下载延迟
      if (!upgradeState.inAutoBackground) {
        message.error(error);
        const delayTime = 30 * 1;
        updateUpgradeState({
          limitDelayTime: delayTime
        });
        countDownLimitTime(delayTime);
      }
    }
  }

  function handleUpdateAvailable() {
    if (upgradeState.limitDelayTime) {
      return;
    }
    if (!canUpdateAvailable.current) return;
    canUpdateAvailable.current = false;
    setTimeout(() => {
      canUpdateAvailable.current = true;
    }, 2000);
    checkAndDownload();
  }

  function handleStateClear(data: any, type = "") {
    const newData = {
      ...data,
      data: { ...data?.data, percent: 0 },
      type
    };
    updateUpgradeState({
      upgradeData: newData
    });
  }

  return (
    <Modal
      width={340}
      maskClosable={false}
      visible={upgradeState?.visible}
      closable={!forceUpdate}
      onCancel={onCancel}
      footer={null}
      centered={true}
      bodyStyle={{
        padding: "0"
      }}
      className={`upgrade-modal-wrap-v2 ${
        (upgradeState?.textChainList || []).length < 2 ? "small" : ""
      }`}
    >
      <div
        className={`upgrade-modal-content ${
          upgradeType === "updateDownloading" ? "downloading" : ""
        }`}
      >
        <div className="title-wrap">
          {popTitleComp}
          <div className="version-info">{versionInfoComp}</div>
        </div>
        <div className="release-wrap" style={relaseNotesStyle}>
          <div className="title ellipsis">{title}</div>
          <ul>
            {releaseNotesTxt.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
        {upgradeState?.textChainList?.length ? (
          <div className="link-wrap">
            {upgradeState?.textChainList.map((i: any, idx: number) => {
              return (
                <div className="item-link" key={`link-${idx}`} onClick={() => handleOpenUrl(i)}>
                  <span className="text">{i.title}</span>
                  {!!i?.url && <IconFont type="iconic_pc_LineRight" />}
                </div>
              );
            })}
          </div>
        ) : null}
        {buttonComp}
      </div>
    </Modal>
  );
}

function mapStateToProps({ user, chat }: any) {
  const { userInfo, upgradeState } = user;
  const { mainWinShow } = chat;
  return {
    userInfo,
    mainWinShow,
    upgradeState
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateUpgradeState: (data: any) => {
      dispatch({ type: "user/updateUpgradeState", payload: data });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UpgradeModal);
